import { FC } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';
import Skeleton from 'react-loading-skeleton';

import { Tooltip } from './tooltip';

interface LabelProps {
  children: string;
  dataTestId: string;
}

const Label: FC<React.PropsWithChildren<LabelProps>> = ({ children, dataTestId }) => {
  return (
    <dt className="stihl-text-body-default-bold max-w-40" data-testid={`${dataTestId}-label`}>
      {children}
    </dt>
  );
};

interface ValueProps {
  children?: string;
  dataTestId: string;
  isAlternativeBrand?: boolean;
}

const Value: FC<React.PropsWithChildren<ValueProps>> = ({ children, dataTestId, isAlternativeBrand }) => {
  const { t } = useTranslation();

  return (
    <dd
      className={cx('stihl-text-body-default-reg', !children && 'text-secondary-dark')}
      data-testid={`${dataTestId}-${children ? 'value' : 'empty'}`}
    >
      {children ??
        (isAlternativeBrand ? t('toolShed_overview_listItem_label_dataNotSet') : t('toolShed_overview_listItem_label_dataNotAvailable'))}
    </dd>
  );
};

interface LabelValueProps {
  label: string;
  dataTestId: string;
  value?: string;
  tooltip?: {
    id: string;
    body: string;
  };
  isLoading?: boolean;
  isAlternativeBrand?: boolean;
}

export const LabelValue: FC<React.PropsWithChildren<LabelValueProps>> = ({
  label,
  dataTestId,
  value,
  tooltip,
  isLoading = false,
  isAlternativeBrand = false,
}) => {
  if (isLoading) {
    return <Skeleton />;
  }

  const _value = (
    <Value dataTestId={dataTestId} isAlternativeBrand={isAlternativeBrand}>
      {value}
    </Value>
  );

  return (
    <>
      <Label dataTestId={dataTestId}>{label}</Label>
      {tooltip ? <Tooltip id={dataTestId} content={tooltip.body} anchor={_value} /> : _value}
    </>
  );
};
